import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { ScApiService } from '@sc-ui';
import { Subject } from 'rxjs';
import Chart from 'chart.js/auto';
import { AppChatsService } from 'src/services/app-chats.service';
import { NotificationsService } from 'src/services/notifications.service';
import { UserService } from 'src/services/user.service';
import { DemoService } from 'src/services/demo.service';
import { LocalizationService } from 'src/services/localization.service';
import { fadeIn } from 'src/transitions';
import { texts } from './dashboard.texts';
import { demoPoolKpis, demoPoolWeeks } from '../pool/pool.demo';

@Component({
  standalone: false,
  selector: 'app-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss'],
  animations: [fadeIn]
})
export class DashboardComponent implements OnInit, OnDestroy {

  texts = texts;

  state = 'out';

  destroy$ = new Subject<any>();

  intro: any;

  project: any;

  newProjects: any = [];

  hasMoreNewProjects = false;

  isLoading = true;

  projects: any;

  hasMoreProjects = false;

  notificationCount: any = 0;

  projectCount = 0;

  pageSize = 5;

  reporting: any;

  // ?obfuscate shows the hardcoded demo content instead of the company's data
  obfuscate = false;

  preparedProjects: any = [];

  runningProjects: any = [];

  preparedMoreCount = 0;

  runningMoreCount = 0;

  constructor(
    private route: ActivatedRoute,
    public userService: UserService,
    private api: ScApiService,
    public notifications: NotificationsService,
    private chatbot: AppChatsService,
    public loc: LocalizationService,
    public user: UserService,
    public demo: DemoService) { }

  // sc-avatar pipes the image through imageSize, which parses it as an absolute url
  private assetUri(path: string) {
    return window.location.origin + '/' + path;
  }

  private agentImage = this.assetUri('assets/img/fav.ico');

  private candidateAvatars(count: number) {
    var avatars = [];
    for (var i = 0; i < count; i++) {
      avatars.push({ Image: this.assetUri('assets/img/avatars/profiles/avatar-' + (i % 8 + 1) + '.jpg') });
    }
    return avatars;
  }

  // Hardcoded dashboard content, shown with ?obfuscate.
  private demoPreparedProjects: any = [
    { Id: 608201, Name: 'Technical Product Owner – Energy Trading', Image: 'https://assets.skillconomy.com/f8fd1e0d-66b3-4e39-8807-20de80c6072d-134332496563273254.jpeg', Status: 'AwaitingOrder' },
    { Id: 608202, Name: 'Management Consultant', Image: 'https://assets.skillconomy.com/10bc8d32-6b56-4888-82a7-41665699518c-133728512804440296.jpeg', Status: 'AwaitingOrder' },
    { Id: 608203, Name: 'IT-Security Specialist', Image: 'https://assets.skillconomy.com/74e3ed2c-e5b8-4777-bbd5-e0a9563f190e-134069780725548167.jpeg', Status: 'AwaitingOrder' }
  ];

  private demoRunningProjects: any = [
    { Id: 608101, Name: 'Prozessingenieur', Image: 'https://assets.skillconomy.com/project-2051-132984794561556886.jpg', Status: 'Sourcing', CandidateCount: 24, Candidates: this.candidateAvatars(4) },
    { Id: 608102, Name: 'Cloud-Architekt Entra ID', Image: 'https://assets.skillconomy.com/3c669217-877c-4447-9730-8b5a7af4bcb5-134171880384080234.jpeg', Status: 'Sourcing', CandidateCount: 31, Candidates: this.candidateAvatars(6) },
    { Id: 608103, Name: 'Senior Key Account Manager', Image: 'https://assets.skillconomy.com/6dd12e75-88c1-4282-b19a-4f2e9b5c49c9-133694868301887968.jpeg', Status: 'Sourcing', CandidateCount: 12, Candidates: this.candidateAvatars(3) }
  ];

  // Hardcoded – no API and no localization yet. Suggestions are acted on, the
  // activities below them are just reported.
  suggestions: any = [
    {
      Author: { FirstName: 'skillconomy', LastName: 'Agent', Image: this.agentImage },
      Timestamp: 'vor 1 Stunde',
      Text: 'hat die Potenzialanalyse für die Stelle Senior Software Engineer – Java, C++ & AI-Assisted Development ' +
        'fertiggestellt: Es wurden 168 passende Talente gefunden.',
      ConfirmLabel: 'Jetzt ansprechen',
      DeclineLabel: 'Verwerfen',
      ConfirmedText: 'Ansprache gestartet.'
    },
    {
      Author: { FirstName: 'Miriam', LastName: 'Vogt', Image: this.assetUri('assets/img/avatars/profiles/avatar-1.jpg') },
      Timestamp: 'Gestern, 16:42',
      Text: 'hat eine neue Position als Head of Engineering bei der Nexura AG begonnen.',
      Message: 'Hallo Miriam, herzlichen Glückwunsch zur neuen Position als Head of Engineering bei Nexura! ' +
        'Wir bauen unser Engineering-Team gerade aus – falls du dich in einiger Zeit noch einmal umsehen ' +
        'möchtest, melde ich mich gerne wieder.',
      ConfirmLabel: 'Senden',
      DeclineLabel: 'Verwerfen',
      ConfirmedText: 'Nachricht gesendet.'
    },
    {
      Author: { FirstName: 'Lena', LastName: 'Hofmann', Image: this.assetUri('assets/img/avatars/profiles/avatar-4.jpg') },
      Timestamp: 'vor 4 Stunden',
      Text: 'feiert heute ihr fünfjähriges Jubiläum bei der Kraftwerk AG.',
      Message: 'Hallo Lena, fünf Jahre bei Kraftwerk – Glückwunsch! Nach so einer Zeit lohnt sich oft ein Blick ' +
        'nach vorn: Wir besetzen gerade die Stelle Senior Software Engineer – Java, C++ & AI-Assisted Development. ' +
        'Hättest du Lust auf einen Austausch?',
      ConfirmLabel: 'Senden',
      DeclineLabel: 'Verwerfen',
      ConfirmedText: 'Nachricht gesendet.'
    },
    {
      Author: { FirstName: 'Philipp', LastName: 'Reuter', Image: this.assetUri('assets/img/avatars/profiles/avatar-3.jpg') },
      Timestamp: 'Gestern, 14:05',
      Text: 'arbeitet bei Vantor Systems – dort wurde ein Stellenabbau angekündigt.',
      Message: 'Hallo Philipp, ich habe gelesen, dass es bei Vantor gerade unruhig zugeht. Falls du dich neu ' +
        'orientieren möchtest: Bei uns ist die Stelle Senior Software Engineer – Java, C++ & AI-Assisted ' +
        'Development offen. Ich erzähle dir gerne mehr dazu.',
      ConfirmLabel: 'Senden',
      DeclineLabel: 'Verwerfen',
      ConfirmedText: 'Nachricht gesendet.'
    },
    {
      Author: { FirstName: 'Aylin', LastName: 'Demir', Image: this.assetUri('assets/img/avatars/profiles/avatar-8.jpg') },
      Timestamp: 'vor 2 Tagen',
      Text: 'hat auf der SecCon einen Vortrag über Qualitätssicherung in der ATMP-Produktion gehalten.',
      Message: 'Hallo Aylin, dein Vortrag auf der SecCon zur ATMP-Qualitätssicherung trifft genau unser Thema. ' +
        'Wir besetzen aktuell die Position VP Quality Assurance – Global Pharma Operations. ' +
        'Wäre ein Gespräch für dich interessant?',
      ConfirmLabel: 'Senden',
      DeclineLabel: 'Verwerfen',
      ConfirmedText: 'Nachricht gesendet.'
    },
    {
      Author: { FirstName: 'skillconomy', LastName: 'Agent', Image: this.agentImage },
      Timestamp: 'vor 3 Stunden',
      Text: 'hat die Jobsite für die Stelle VP Quality Assurance – Global Pharma Operations erstellt und wartet ' +
        'auf deine Freigabe.',
      ConfirmLabel: 'Jobsite prüfen',
      DeclineLabel: 'Verwerfen',
      ConfirmedText: 'Jobsite geöffnet.'
    },
    {
      Author: { FirstName: 'Jonas', LastName: 'Brand', Image: this.assetUri('assets/img/avatars/profiles/avatar-2.jpg') },
      Timestamp: 'Gestern, 11:20',
      Text: 'ist im Talentpool als wechselbereit markiert und passt zur Stelle Senior Software Engineer – ' +
        'Java, C++ & AI-Assisted Development.',
      ConfirmLabel: 'Profil ansehen',
      DeclineLabel: 'Verwerfen',
      ConfirmedText: 'Profil geöffnet.'
    }
  ];

  activities: any = [
    {
      Author: { FirstName: 'Sandra', LastName: 'Keller', Image: this.assetUri('assets/img/avatars/profiles/avatar-5.jpg') },
      Timestamp: 'vor 2 Stunden',
      Text: 'hat sich für den Talentpool registriert.'
    },
    {
      Author: { FirstName: 'Tobias', LastName: 'Ritter', Image: this.assetUri('assets/img/avatars/profiles/avatar-7.jpg') },
      Timestamp: 'vor 5 Stunden',
      Text: 'hat sich auf die Stelle Senior Software Engineer – Java, C++ & AI-Assisted Development beworben.'
    },
    {
      Author: { FirstName: 'skillconomy', LastName: 'Agent', Image: this.agentImage },
      Timestamp: 'Gestern, 09:15',
      Text: 'hat die Ansprache für das Projekt VP Quality Assurance – Global Pharma Operations abgeschlossen.'
    }
  ];

  poolChart?: Chart;

  // the pool card only renders in demo mode, so the chart follows its canvas
  @ViewChild('poolChart')
  set poolChartCanvas(canvas: ElementRef<HTMLCanvasElement>) {
    this.poolChart?.destroy();
    this.poolChart = canvas ? this.createPoolChart(canvas.nativeElement) : undefined;
  }

  poolWeeks = demoPoolWeeks;

  poolKpis = demoPoolKpis;

  // the newest signals of the pool's activity feed, each with a label that fits one line
  poolActivities: any = [];

  // Hardcoded - no API yet. Short labels for the signals of the demo pool feed, by activity id.
  private poolSignalLabels = {
    9211: 'Wechselbereit',
    9212: 'Fachlicher Post',
    9213: 'Bewerbung begonnen',
    9214: 'Event-Zusage',
    9215: 'Umstrukturierung',
    9216: 'Neue Zertifizierung',
    9217: 'Team-Abgänge',
    9218: 'Neue Position',
    9219: 'Keine Reaktion',
    9220: 'Passt zu Projekt',
    9221: 'Hohes Engagement',
    9222: 'Wechselbereit',
    9315: 'Auf Stelle gematcht'
  };

  private demoPreparedMoreCount = 2;

  private demoRunningMoreCount = 5;

  confirmAction(item) {
    item.State = 'confirmed';
  }

  declineAction(item) {
    item.State = 'declined';
  }

  ngOnInit() {

    this.obfuscate = this.route.snapshot.queryParams['obfuscate'] != null;

    if (this.obfuscate) {
      this.preparedProjects = this.demoPreparedProjects;
      this.runningProjects = this.demoRunningProjects;
      this.preparedMoreCount = this.demoPreparedMoreCount;
      this.runningMoreCount = this.demoRunningMoreCount;
      this.isLoading = false;
      this.state = 'in';
      return;
    }

    this.api.get('projects/reporting?ownership=All').subscribe(d => {
      this.reporting = d;
      this.fadeInIfComplete();
    });

    // the pool card only renders in demo mode
    if (this.demo.isActive) {
      this.api.get('pool/activities?page=1&pageSize=10').subscribe((d: any) => {
        this.poolActivities = d.Items
          .filter(a => a.Content == 'pool_signal')
          .slice(0, 3)
          .map(a => Object.assign({}, a, { SignalLabel: this.poolSignalLabels[a.Id] }));
      });
    }

    this.api.get('dashboard').subscribe(d => {
      this.intro = d;
      this.fadeInIfComplete();
    });

    this.api.get(`projects/search?pageSize=${this.pageSize}&status=AwaitingOrder`)
      .subscribe((p: any) => {
        this.preparedProjects = p.Projects;
        this.newProjects = p.Projects;
        this.preparedMoreCount = p.TotalCount - p.Projects.length;
        this.hasMoreNewProjects = this.preparedMoreCount > 0;
        this.isLoading = false;
        this.fadeInIfComplete();
      });

    this.api.get('projects/count')
      .subscribe((p: any) => {
        this.projectCount = p.Total;
      });

    this.api.get(`projects/search?pageSize=${this.pageSize}&status=Sourcing&orderBy=Applications`).subscribe((d: any) => {
      this.runningProjects = d.Projects;
      this.projects = d.Projects;
      this.runningMoreCount = d.TotalCount - d.Projects.length;
      this.hasMoreProjects = this.runningMoreCount > 0;
      this.fadeInIfComplete();
    });
  }

  createPoolChart(canvas: HTMLCanvasElement) {

    return new Chart(canvas, {
      type: 'bar',
      data: {
        labels: this.poolWeeks.map(w => w.Label),
        datasets: [
          {
            label: texts.pool.size[this.loc.language],
            data: this.poolWeeks.map(w => w.Value),
            backgroundColor: 'rgba(0,98,215,0.8)',
            borderColor: 'rgba(0,98,215,0.8)',
            borderRadius: 8,
            // half the default bar width (0.9)
            barPercentage: 0.45
          }
        ]
      },
      options: {
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: false
          }
        },
        scales: {
          y: {
            beginAtZero: true
          }
        }
      }
    });
  }

  fadeInIfComplete() {
    if (this.projects != null && this.reporting != null && this.intro != null) {
      this.state = 'in';
    }
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.poolChart?.destroy();
    this.chatbot.cancelBotChat();
  }

  getImageIndex(item) {
    return item.Id % 3 + 1;
  }

  newProject() {
    this.chatbot.newProjectChat();
  }

  showNotifications() {
    this.notifications.showModal();
  }
}
